import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { DateField, SmartSelect } from '../dropdown';
import type {
  CustomerVendorSubscription,
  BillingCadence,
  SubscriptionStatus,
} from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { updateVendorSubscription } from '../../services/vendorSubscriptionService';
import './reconciliationPanel.css';

const CADENCE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annually' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface Props {
  open: boolean;
  subscription: CustomerVendorSubscription;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit a subscription — the commitment (authoritative internal record). Used to
 * maintain it deliberately when a customer changes seats/term (so the next
 * reconcile matches both the vendor import and the billing schedule). Never
 * auto-derived; this is the explicit human update.
 */
export const SubscriptionEditDialog: React.FC<Props> = ({
  open,
  subscription,
  onClose,
  onSaved,
}) => {
  const [seats, setSeats] = useState<number>(subscription.seats);
  const [cadence, setCadence] = useState<BillingCadence>(subscription.billingCadence);
  const [renewalDate, setRenewalDate] = useState<string>(
    (subscription.renewalDate ?? '').slice(0, 10),
  );
  const [status, setStatus] = useState<SubscriptionStatus>(subscription.status);
  const [label, setLabel] = useState<string>(subscription.label ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!Number.isFinite(seats) || seats < 0) {
      setError('Seats must be a non-negative number.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateVendorSubscription(subscription.id, {
        seats,
        billingCadence: cadence,
        renewalDate: renewalDate || null,
        status,
        label: label.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-label="Edit subscription"
      PaperProps={{
        sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
      }}
    >
      <SidePanel
        title="Edit subscription"
        submitLabel={saving ? 'Saving…' : 'Save'}
        isValid={!saving && Number.isFinite(seats) && seats >= 0}
        onClose={() => {
          if (!saving) onClose();
        }}
        onSubmit={submit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <p className="recon-dialog__hint">
          {subscription.vendorProductName ?? 'Vendor product'} — the commitment we
          reconcile against. Update it when the customer's count or term changes.
        </p>

        <FormField label="Label">
          <input
            type="text"
            value={label}
            placeholder="e.g. tenant name — optional"
            onChange={(e) => setLabel(e.target.value)}
          />
        </FormField>

        <FormField label="Seats">
          <input
            type="number"
            min={0}
            value={seats}
            onChange={(e) => setSeats(Number(e.target.value))}
          />
        </FormField>

        <SmartSelect
          label="Billing cadence"
          value={cadence}
          options={CADENCE_OPTIONS}
          onChange={(v) => {
            if (v) setCadence(v as BillingCadence);
          }}
        />

        <DateField
          label="Renewal date"
          value={renewalDate || null}
          onChange={(iso) => setRenewalDate(iso ?? '')}
        />

        <SmartSelect
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            if (v) setStatus(v as SubscriptionStatus);
          }}
        />
      </SidePanel>
    </Dialog>
  );
};
